import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { unitSelectors } from 'Core/realtime/redux/unitSlice';

/**
 * Custom hook to safely and efficiently read Realtime Units from the RTK Array Slice.
 *
 * @param {Object} options 
 * @param {Array<string>} [options.trackFields=[]] - Specific unit fields to monitor for changes (e.g. ['label', 'installed']). 
 *        If empty, it tracks object identity (which mutates constantly via websockets) so tracking fields is recommended for UI Grid stability.
 * @returns {Array<Object>} The array of memoized Units.
 */
export const useRealtimeUnits = (options = {}) => {
    const { trackFields = [] } = options;

    // Retrieve the normalized raw array in O(1).
    // Note: The selector recalculates array-reference locally if any unit mutates.
    const rawUnits = useSelector(state => unitSelectors.selectAll(state));

    const previousCacheRef = useRef([]);

    // We intercept the raw RTK output and manually test for field equity 
    // to shield the React component from redundant Object reference changes.
    const optimizedUnits = useMemo(() => {
        // If no fields are provided to track, we gracefully fallback to returning the live Redux identity array.
        if (!trackFields || trackFields.length === 0) {
            previousCacheRef.current = rawUnits;
            return rawUnits;
        }

        const prevUnits = previousCacheRef.current;
        let requiresDomUpdate = false;

        // Validation 1: Size variation is an immediate update
        if (prevUnits.length !== rawUnits.length) {
            requiresDomUpdate = true;
        } else {
            // Validation 2: Check deep equity exclusively on the `trackFields` layer
            for (let i = 0; i < rawUnits.length; i++) {
                const liveUnit = rawUnits[i];
                const cachedUnit = prevUnits[i];

                // Since we rely on Redux's strict ID ordering, if IDs mismatch
                // it means the order changed or objects swapped. Update required.
                if (String(liveUnit.id) !== String(cachedUnit.id)) {
                    requiresDomUpdate = true;
                    break;
                }

                // Check deep property mutations on requested fields
                for (const field of trackFields) {
                    if (liveUnit[field] !== cachedUnit[field]) {
                        requiresDomUpdate = true;
                        break;
                    }
                }

                if (requiresDomUpdate) break;
            }
        }

        // If the configured field values mutated, sync the cache and force a new React Reference
        if (requiresDomUpdate) {
            previousCacheRef.current = [...rawUnits];
            return previousCacheRef.current;
        }

        // No configured fields mutated. Return historical React Object Reference (O(1) No-op)
        return previousCacheRef.current;

    }, [rawUnits, trackFields]); // Re-calculates ONLY when RTK pushes a new array or track schema changes

    return optimizedUnits;
};

export default useRealtimeUnits;
